import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppCurrency,
  CreateRecurringExpenseRequest,
  GenerateRecurringResponse,
  RecurringExpense,
  SetRecurringMonthAmountRequest,
  UndoGeneratedRecurringRequest,
  UndoGeneratedRecurringResponse,
  UpcomingRecurringResponse,
  UpdateRecurringExpenseRequest,
} from '@financial/shared';
import { api } from '../../lib/api';
import { currentCurrency } from '../../lib/format';
import { notify } from '../../store/notifications';

const KEY = ['recurring-expenses'];

async function list(): Promise<RecurringExpense[]> {
  const res = await api.get<RecurringExpense[]>('/recurring-expenses');
  return res.data;
}

async function upcoming(month: string | undefined, currency: AppCurrency) {
  const res = await api.get<UpcomingRecurringResponse>('/recurring-expenses/upcoming', {
    params: { ...(month ? { month } : {}), currency },
  });
  return res.data;
}

export function useRecurring() {
  return useQuery({ queryKey: KEY, queryFn: list });
}

/**
 * Lo que falta por ocurrir este mes. Viene derivado del backend (recurrentes
 * activos aún no generados), así que respeta los ajustes de monto por mes y no
 * lista lo que ya se registró.
 */
export function useUpcomingRecurring(month?: string, enabled = true) {
  const currency = currentCurrency();
  return useQuery({
    queryKey: [...KEY, 'upcoming', month ?? 'current', currency],
    enabled,
    queryFn: () => upcoming(month, currency),
  });
}

export interface UpcomingRecurringCurrencyState {
  currency: AppCurrency;
  data?: UpcomingRecurringResponse;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * La lista de movimientos fijos puede contener varias monedas. Consultarlas por
 * separado evita interpretar como "registrada" una regla que simplemente no
 * pertenecía a la moneda activa cuando se pidió `/upcoming`.
 */
export function useUpcomingRecurringCurrencies(
  month: string | undefined,
  currencies: AppCurrency[],
  enabled = true,
): UpcomingRecurringCurrencyState[] {
  const uniqueCurrencies = [...new Set(currencies)].sort();
  const queries = useQueries({
    queries: uniqueCurrencies.map((currency) => ({
      queryKey: [...KEY, 'upcoming', month ?? 'current', currency],
      enabled,
      queryFn: () => upcoming(month, currency),
    })),
  });

  return uniqueCurrencies.map((currency, index) => ({
    currency,
    data: queries[index].data,
    isLoading: queries[index].isPending,
    isFetching: queries[index].isFetching,
    isSuccess: queries[index].isSuccess,
    isError: queries[index].isError,
  }));
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['transactions'] });
    void qc.invalidateQueries({ queryKey: ['transfers'] });
    void qc.invalidateQueries({ queryKey: ['net-worth'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
    void qc.invalidateQueries({ queryKey: ['credit-card'] });
    void qc.invalidateQueries({ queryKey: ['savings-goals'] });
  };
}

export function useCreateRecurring() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: CreateRecurringExpenseRequest) => {
      const res = await api.post<RecurringExpense>('/recurring-expenses', body);
      return res.data;
    },
    onSuccess: (recurring) => {
      invalidate();
      notify.success('Movimiento fijo creado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete(`/recurring-expenses/${recurring.id}`);
            invalidate();
            notify.info('Movimiento fijo deshecho');
          },
        },
      });
    },
  });
}

export function useUpdateRecurring() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdateRecurringExpenseRequest }) => {
      const res = await api.patch<RecurringExpense>(`/recurring-expenses/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      notify.success('Movimiento fijo actualizado');
    },
  });
}

/**
 * Ajusta el monto de un mes concreto (nómina variable, bonos).
 * `amount: null` quita el ajuste y ese mes vuelve al monto base.
 */
export function useSetRecurringMonthAmount() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; month: string; amount: number | null }) => {
      const body: SetRecurringMonthAmountRequest = { amount: vars.amount };
      const res = await api.put<RecurringExpense>(
        `/recurring-expenses/${vars.id}/amounts/${vars.month}`,
        body,
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteRecurring() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/recurring-expenses/${id}`);
    },
    onSuccess: invalidate,
  });
}

export function useRunRecurring() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<GenerateRecurringResponse>('/recurring-expenses/run');
      return res.data;
    },
    // El lote actual puede crear reglas anteriores y después encontrar una sin
    // saldo. Refrescamos también tras error para no dejar saldos o pendientes
    // obsoletos en pantalla.
    onSettled: invalidate,
  });
}

/** Registra manualmente un fijo con la fecha de hoy; el backend lo marca para que el cron no lo duplique. */
export function useGenerateRecurringOne() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, month }: { id: string; month?: string }) => {
      const res = await api.post<GenerateRecurringResponse>(
        `/recurring-expenses/${id}/generate`,
        undefined,
        {
          params: month ? { month } : undefined,
        },
      );
      return res.data;
    },
    onSuccess: (result) => {
      invalidate();
      const occurrence = result.occurrence;
      if (!occurrence) return;
      notify.success('Movimiento fijo registrado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            const body: UndoGeneratedRecurringRequest = {
              month: occurrence.month,
              artifactKind: occurrence.artifactKind,
              artifactId: occurrence.artifactId,
            };
            await api.post<UndoGeneratedRecurringResponse>(
              `/recurring-expenses/${occurrence.recurringExpenseId}/generate/undo`,
              body,
            );
            invalidate();
            notify.info('Registro deshecho; vuelve a aparecer como pendiente');
          },
        },
      });
    },
  });
}
